import React, { useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { AlertCircle, Check, Eye, EyeOff } from 'lucide-react';
import { Logo } from '@/components/Logo';
import { PasswordStrength } from '@/components/PasswordStrength';
import api from '@/lib/api';

export default function ResetPasswordPage() {
  const [searchParams] = useSearchParams();
  const token = searchParams.get('token') || '';
  const [password, setPassword] = useState('');
  const [confirm, setConfirm] = useState('');
  const [showPw, setShowPw] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    if (password.length < 6) { setError('Password must be at least 6 characters'); return; }
    if (password !== confirm) { setError('Passwords do not match'); return; }
    if (!token) { setError('Invalid or missing reset token. Please request a new reset link.'); return; }
    setLoading(true);
    try {
      await api.resetPassword(token, password);
      setSuccess(true);
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to reset password');
    }
    setLoading(false);
  };

  return (
    <div className="min-h-screen bg-[#F8FAFC] flex items-center justify-center p-6" data-testid="reset-password-page">
      <div className="w-full max-w-sm">
        <div className="mb-8">
          <Logo size="sm" />
        </div>

        {!success ? (
          <>
            <h1 className="font-heading text-2xl font-bold text-slate-900 tracking-tight mb-1">Set new password</h1>
            <p className="text-sm text-slate-500 mb-6">Choose a strong password for your account.</p>
            {error && (
              <div className="flex items-center gap-2 p-3 mb-4 bg-red-50 border border-red-200 rounded-sm text-sm text-red-700" data-testid="reset-error">
                <AlertCircle className="w-4 h-4 shrink-0" /> {error}
              </div>
            )}
            <form onSubmit={handleSubmit} className="space-y-4">
              <div>
                <Label className="text-xs font-medium text-slate-700 uppercase tracking-wide">New Password</Label>
                <div className="relative mt-1">
                  <Input
                    type={showPw ? 'text' : 'password'}
                    value={password}
                    onChange={e => setPassword(e.target.value)}
                    placeholder="Min 6 characters"
                    className="rounded-sm pr-10"
                    required
                    data-testid="reset-password-input"
                  />
                  <button type="button" onClick={() => setShowPw(!showPw)} className="absolute right-2.5 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600">
                    {showPw ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                  </button>
                </div>
                <PasswordStrength password={password} />
              </div>
              <div>
                <Label className="text-xs font-medium text-slate-700 uppercase tracking-wide">Confirm Password</Label>
                <Input
                  type="password"
                  value={confirm}
                  onChange={e => setConfirm(e.target.value)}
                  placeholder="Re-enter password"
                  className="mt-1 rounded-sm"
                  required
                  data-testid="reset-confirm-input"
                />
              </div>
              <Button type="submit" className="w-full bg-slate-900 hover:bg-slate-800 text-white rounded-sm h-10 text-sm font-medium" disabled={loading} data-testid="reset-submit-btn">
                {loading ? 'Resetting...' : 'Reset Password'}
              </Button>
            </form>
          </>
        ) : (
          <div className="text-center" data-testid="reset-success">
            <div className="w-12 h-12 bg-emerald-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <Check className="w-6 h-6 text-emerald-600" />
            </div>
            <h1 className="font-heading text-2xl font-bold text-slate-900 tracking-tight mb-2">Password reset</h1>
            <p className="text-sm text-slate-500 mb-6">Your password has been reset successfully. All existing sessions have been revoked for security.</p>
            <Link to="/login">
              <Button className="bg-slate-900 hover:bg-slate-800 text-white rounded-sm h-10 text-sm font-medium px-8" data-testid="reset-go-to-login">
                Sign In
              </Button>
            </Link>
          </div>
        )}

        <p className="text-sm text-slate-500 text-center mt-6">
          <Link to="/login" className="text-[#0047AB] font-medium hover:underline">Back to sign in</Link>
        </p>
      </div>
    </div>
  );
}
